'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Dialog } from '@base-ui-components/react/dialog';
import { useOverlayStackPosition } from './drawer-stack-context';
import styles from './modal.module.css';

export type ModalRef<T = unknown> = {
  close: (result?: T) => void;
  setDisableClose: (value: boolean) => void;
};

export interface OpenModalOptions<T> {
  content: ReactNode | ((modalRef: ModalRef<T>) => ReactNode);
  disableClose?: boolean;
  defaultCloseResult?: T;
  confirmAutofocus?: boolean;
  panelClassName?: string;
  backdropClassName?: string;
  /**
   * How the panel is presented. 'modal' (default) is a centered dialog; 'drawer'
   * is a right-side slide-in panel — the design-system default for record
   * detail / edit forms (short confirmations should stay 'modal').
   */
  presentation?: 'modal' | 'drawer';
}

type OpenModal = <T>(options: OpenModalOptions<T>) => Promise<T | undefined>;

interface ModalState {
  id: number;
  content: ReactNode | ((modalRef: ModalRef<unknown>) => ReactNode);
  disableClose: boolean;
  defaultCloseResult: unknown;
  confirmAutofocus: boolean;
  panelClassName?: string;
  backdropClassName?: string;
  presentation: 'modal' | 'drawer';
  resolve: (value: unknown) => void;
  close: (result?: unknown) => void;
  setDisableClose: (value: boolean) => void;
}

const ModalContext = createContext<OpenModal | null>(null);

export function useModal(): OpenModal {
  const ctx = useContext(ModalContext);
  if (!ctx) {
    throw new Error('useModal must be used inside <ModalProvider>');
  }

  return ctx;
}

export function ModalProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ModalState | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const openCounterRef = useRef(0);

  // Registers with the same shared overlay stack `Drawer` uses (see
  // drawer-stack-context.tsx). Only one modal is ever open at once (`open`
  // above resolves and replaces any current `state`), so nothing today opens
  // a SECOND modal over this one — but this Modal is very much itself opened
  // ON TOP of an already-open `Drawer` (every `useConfirm()` call site that
  // lives inside a Drawer, e.g. audiences/org-groups/membership-panel.tsx).
  // Pushing here is what lets that Drawer discover it's no longer topmost and
  // stop reacting to Esc/Tab out from under this dialog. `isTopmost`/
  // `isFullyCovered` are consumed below defensively, so this Modal would ALSO
  // correctly hide if something were ever opened on top of it — there's no
  // live call site for that today, but the mechanism costs nothing extra.
  //
  // `coversPanel: false`: unlike `Drawer`, a Modal is a small centered card,
  // not an edge-to-edge replacement of the view — the Drawer (or page) it was
  // raised over should stay visible, dimmed by THIS Modal's own backdrop,
  // rather than blanking out entirely underneath it. See
  // `DrawerStack.isPanelHidden`'s own comment.
  const { isTopmost, isFullyCovered } = useOverlayStackPosition(Boolean(state), {
    hasBackdrop: true,
    coversPanel: false,
  });

  const open = useCallback(<T,>(options: OpenModalOptions<T>): Promise<T | undefined> => {
    return new Promise<T | undefined>((resolve) => {
      const id = ++openCounterRef.current;
      const closeResult = options.defaultCloseResult as T | undefined;

      const close = (result?: unknown) => {
        setState((current) => {
          if (!current || current.id !== id) {
            return current;
          }

          current.resolve(result);
          return null;
        });
      };

      const setDisableClose = (value: boolean) => {
        setState((current) =>
          current && current.id === id ? { ...current, disableClose: value } : current,
        );
      };

      setState((current) => {
        if (current) {
          current.resolve(undefined);
        }

        return {
          id,
          content: options.content as ReactNode | ((modalRef: ModalRef<unknown>) => ReactNode),
          disableClose: options.disableClose ?? false,
          defaultCloseResult: closeResult,
          confirmAutofocus: options.confirmAutofocus ?? false,
          panelClassName: options.panelClassName,
          backdropClassName: options.backdropClassName,
          presentation: options.presentation ?? 'modal',
          resolve: (value) => resolve(value as T | undefined),
          close,
          setDisableClose,
        };
      });
    });
  }, []);

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (nextOpen || !state) {
        return;
      }

      if (state.disableClose) {
        return;
      }

      state.close(state.defaultCloseResult);
    },
    [state],
  );

  const renderedContent = state
    ? typeof state.content === 'function'
      ? (state.content as (value: ModalRef<unknown>) => ReactNode)({
          close: state.close,
          setDisableClose: state.setDisableClose,
        })
      : state.content
    : null;

  useEffect(() => {
    if (!state?.confirmAutofocus) {
      return;
    }

    // window.setTimeout returns a DOM number here, not Node's Timeout — using
    // ReturnType<typeof window.setTimeout> resolves to the Node overload and fails tsc.
    const timeoutIds = new Set<number>();
    const frameIds = new Set<number>();

    const focusFromModal = () => {
      const confirmButton = dialogRef.current?.querySelector<HTMLElement>('[data-modal-confirm]');
      const fallbackButton = dialogRef.current?.querySelector<HTMLElement>('button');

      if (confirmButton) {
        confirmButton.focus();
        return true;
      }

      if (fallbackButton) {
        fallbackButton.focus();
      }

      return Boolean(fallbackButton);
    };

    const scheduleAutofocusAttempt = (remaining: number, delay: number) => {
      const timeoutId = window.setTimeout(() => {
        timeoutIds.delete(timeoutId);

        const firstFrameId = requestAnimationFrame(() => {
          frameIds.delete(firstFrameId);

          const secondFrameId = requestAnimationFrame(() => {
            frameIds.delete(secondFrameId);
            attemptAutofocus(remaining);
          });
          frameIds.add(secondFrameId);
        });
        frameIds.add(firstFrameId);
      }, delay);
      timeoutIds.add(timeoutId);
    };

    const attemptAutofocus = (remaining: number) => {
      if (!state?.confirmAutofocus) {
        return;
      }

      if (focusFromModal()) {
        return;
      }

      if (remaining > 0) {
        scheduleAutofocusAttempt(remaining - 1, 16);
      }
    };

    scheduleAutofocusAttempt(4, 0);

    return () => {
      timeoutIds.forEach((timeoutId) => {
        window.clearTimeout(timeoutId);
      });
      timeoutIds.clear();

      frameIds.forEach((frameId) => {
        cancelAnimationFrame(frameId);
      });
      frameIds.clear();
    };
  }, [state?.id, state?.confirmAutofocus]);

  return (
    <ModalContext.Provider value={open}>
      {children}
      <Dialog.Root
        open={Boolean(state)}
        onOpenChange={handleOpenChange}
        disablePointerDismissal={state?.disableClose}
      >
        <Dialog.Portal>
          {/* Both layers come from the shared ladder (src/styles/z-index.css).
              They used to be 1400 apart — backdrop 100, popup 1501 — which let
              the side panel (1000), the mobile side nav (998) and the timeline's
              fullscreen surface (1400) sit ABOVE the scrim while this dialog
              floated above them: a dim overlay that visibly failed to dim.
              A scrim must outrank everything it covers, so --z-modal-backdrop
              now sits above all app chrome. */}
          <Dialog.Backdrop
            // Stable hook for tests. The backdrop is styled with utility
            // classes rather than a CSS module, so there is no `backdrop` class
            // to select on — three test files had gone stale reaching for one.
            data-testid="modal-backdrop"
            className={[
              'fixed inset-0 bg-[var(--modal-overlay-background-default)]',
              state?.backdropClassName,
              isFullyCovered ? styles.hidden : null,
            ]
              .filter(Boolean)
              .join(' ')}
            style={{ zIndex: 'var(--z-modal-backdrop)' as unknown as number }}
            inert={isFullyCovered ? true : undefined}
          />
          <Dialog.Popup
            className={[
              styles.popup,
              state?.presentation === 'drawer' ? styles.drawerPopup : null,
              state?.panelClassName,
              isTopmost ? null : styles.hidden,
            ]
              .filter(Boolean)
              .join(' ')}
            style={{ zIndex: 'var(--z-modal)' as unknown as number }}
            inert={isTopmost ? undefined : true}
          >
            <div ref={dialogRef} className={styles.popupContent}>
              {renderedContent}
            </div>
          </Dialog.Popup>
        </Dialog.Portal>
      </Dialog.Root>
    </ModalContext.Provider>
  );
}
